import { MessageSquare, Printer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useCompany } from '@/lib/CompanyProvider'
import type { Order } from './KanbanCard'

interface QuotePDFModalProps {
  order: Order | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function QuotePDFModal({ order, open, onOpenChange }: QuotePDFModalProps) {
  const { companies, activeCompanyId } = useCompany()
  const activeCompany = companies?.find((c) => c.id === activeCompanyId)

  if (!order) return null

  const handlePrint = () => {
    window.print()
  }

  const handleSendWhatsApp = () => {
    const servicesList = Array.isArray(order.services)
      ? order.services.map((s) => `• ${s.name}: R$ ${Number(s.price).toFixed(2)}`).join('\n')
      : ''

    const text = `Olá, *${order.customer_name}*!\n\nSegue o orçamento para o veículo *${order.vehicle_model}* (Placa: *${order.plate}*):\n\n${servicesList}\n\n*Total: R$ ${Number(order.total).toFixed(2)}*\n\nFicamos no aguardo da sua aprovação para iniciar o serviço!`

    const phoneClean = order.customer_phone.replace(/\D/g, '')
    const whatsappUrl = `https://wa.me/55${phoneClean}?text=${encodeURIComponent(text)}`
    window.open(whatsappUrl, '_blank')
  }

  const createdDate = new Date(order.created_at).toLocaleDateString('pt-BR')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-slate-900 border-slate-800 text-slate-100 p-0 overflow-hidden max-h-[90vh] flex flex-col">
        <DialogHeader className="p-4 border-b border-slate-800 flex flex-row items-center justify-between">
          <DialogTitle className="text-lg font-bold text-white flex items-center gap-2">
            Orçamento comercial
          </DialogTitle>
          <div className="flex items-center gap-2 pr-6">
            <Button
              size="sm"
              variant="outline"
              onClick={handlePrint}
              className="gap-2 border-slate-700 bg-slate-800 hover:bg-slate-700"
            >
              <Printer size={16} /> Imprimir / PDF
            </Button>
            <Button
              size="sm"
              className="gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold"
              onClick={handleSendWhatsApp}
            >
              <MessageSquare size={16} /> Enviar WhatsApp
            </Button>
          </div>
        </DialogHeader>

        <div className="p-6 overflow-y-auto flex-1 bg-slate-950 text-slate-900">
          {/* Documento Estilizado para Impressão */}
          <div
            id="printable-quote"
            className="bg-white p-8 rounded-xl shadow-xl max-w-xl mx-auto border border-slate-200"
          >
            {/* Header da Empresa */}
            <div className="flex justify-between items-start border-b border-slate-200 pb-6 mb-6">
              <div>
                <h1 className="text-2xl font-black tracking-tight text-slate-900 uppercase">
                  {activeCompany?.name || 'AutoWash Pro'}
                </h1>
                <p className="text-xs text-slate-500 mt-1">
                  Estética Automotiva & Lava Jato Especializado
                </p>
              </div>
              <div className="text-right">
                <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-xs font-extrabold rounded-full uppercase">
                  ORÇAMENTO
                </span>
                <p className="text-xs text-slate-400 mt-2">Data: {createdDate}</p>
              </div>
            </div>

            {/* Dados do Cliente e Veículo */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg border border-slate-100 mb-6 text-sm">
              <div>
                <span className="text-xs text-slate-400 font-bold uppercase block">Cliente</span>
                <p className="font-bold text-slate-800">{order.customer_name}</p>
                <p className="text-xs text-slate-500">{order.customer_phone}</p>
              </div>
              <div>
                <span className="text-xs text-slate-400 font-bold uppercase block">
                  Veículo / Placa
                </span>
                <p className="font-bold text-slate-800">{order.vehicle_model}</p>
                <p className="text-xs font-mono font-bold text-blue-600">{order.plate}</p>
              </div>
            </div>

            {/* Tabela de Serviços */}
            <div className="mb-6">
              <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                Serviços Solicitados
              </h2>
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 text-xs text-slate-500 uppercase">
                    <th className="py-2">Item / Descrição</th>
                    <th className="py-2 text-right">Valor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {Array.isArray(order.services) &&
                    order.services.map((svc: any, idx: number) => (
                      <tr key={idx}>
                        <td className="py-3 font-medium text-slate-700">{svc.name}</td>
                        <td className="py-3 text-right font-bold text-slate-900">
                          R$ {Number(svc.price).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Total e Observações */}
            <div className="border-t border-slate-200 pt-4 flex justify-between items-end">
              <div className="text-xs text-slate-400">
                <p> Validade deste orçamento: 7 dias</p>
                <p> O pagamento será realizado após a conclusão.</p>
              </div>
              <div className="text-right">
                <span className="text-xs text-slate-500 block uppercase font-bold">
                  Valor Total
                </span>
                <span className="text-2xl font-black text-blue-600">
                  R$ {Number(order.total).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
